import {
  ArrowRight,
  Badge,
  Button,
  Column,
  Danger,
  getColorValue,
  Icon,
  InfoMarkLine,
  Row,
  Typography,
  useTheme,
} from '@viewup/ui';
import { useGlobalModalStoreBase } from 'app/_hook';
import {
  useGetActiveNBlogAccount,
  useGetNBlogAccounts,
  useGetPenaltyInfo,
  useNBlogAccountsStatus,
} from 'app/viewing/_entities/post';
import dayjs from 'dayjs';
import { useState } from 'react';
import { ManageModal } from './ManageModal';

export const NblogAccountManage = () => {
  const theme = useTheme();
  const { setAlertModal } = useGlobalModalStoreBase((state) => state.actions);

  const { data: activeAccount } = useGetActiveNBlogAccount();
  const { data: accounts } = useGetNBlogAccounts();
  const { data: accountsStatus } = useNBlogAccountsStatus();
  const { data: penaltyInfo } = useGetPenaltyInfo(activeAccount?.accountUuid || '');

  const [isOpenManageModal, setIsOpenManageModal] = useState(false);
  const hasNoAccounts = accountsStatus?.hasNoAccounts;
  const errorAccountCount = accountsStatus?.errorAccountCount || 0;
  const hasNoActiveAccount = !hasNoAccounts && !activeAccount;
  const needSubmissionCount =
    accounts?.reduce((acc, cur) => {
      if (cur.accountUuid === activeAccount?.accountUuid) return acc;
      return acc + (cur?.needSubmission || 0);
    }, 0) || 0;

  return (
    <Column border='1 solid border.secondary' br={12} px={16} py={12}>
      <Row
        alignItems='center'
        as='button'
        data-tour='post-account-manage'
        justifyContent='space-between'
        onClick={() => {
          if (hasNoAccounts) return;
          setIsOpenManageModal(true);
        }}
      >
        <Typography fontType='body2.regular'>연결된 계정</Typography>

        <Row alignItems='center' gap={8}>
          {hasNoAccounts && (
            <Typography color='text.disabled' fontType='body2.regular'>
              추가된 계정이 없어요
            </Typography>
          )}
          {hasNoActiveAccount && (
            <Typography color='text.disabled' fontType='body2.regular'>
              연결된 계정이 없어요
            </Typography>
          )}
          {activeAccount && (
            <Row alignItems='center' gap={8}>
              <Typography color='text.tertiary' fontType='body2.regular'>
                {activeAccount.accountName}
              </Typography>
              <Badge colorVariant='green' content={'적립중'} variant='rectangle' />
            </Row>
          )}
          <Icon size='2XS' source={ArrowRight} />
        </Row>
      </Row>

      {(needSubmissionCount > 0 ||
        errorAccountCount > 0 ||
        penaltyInfo?.isSuspended ||
        penaltyInfo?.hasPenalty) && (
        <Column gap={8} mt={12}>
          {needSubmissionCount > 0 && (
            <Button
              css={{
                justifyContent: 'space-between',
                pointerEvents: 'none',
                width: '100%',
              }}
              rightContent={
                <Typography fontType='body2.regular'>{needSubmissionCount}개</Typography>
              }
              size={'S'}
              variant='secondarySolid.info'
            >
              다른 계정에도 제출이 필요해요!
            </Button>
          )}

          {errorAccountCount > 0 && (
            <Button
              css={{
                justifyContent: 'space-between',
                pointerEvents: 'none',
                width: '100%',
              }}
              rightContent={<Typography fontType='body2.regular'>{errorAccountCount}개</Typography>}
              size={'S'}
              variant='secondarySolid.error'
            >
              확인이 필요한 계정이 있어요!
            </Button>
          )}

          {(penaltyInfo?.isSuspended || penaltyInfo?.hasPenalty) && (
            <Button
              leftContent={
                <Row alignItems='center' gap={4}>
                  <Icon size='2XS' source={Danger} />
                  <Typography fontType='body2.regular'>
                    {penaltyInfo?.isSuspended ? '활동이 제한되었어요' : '활동이 제한될 수 있어요'}
                  </Typography>
                  <Icon
                    css={{ cursor: 'pointer', pointerEvents: 'auto' }}
                    onClick={() => {
                      setAlertModal({
                        content: penaltyInfo.isSuspended
                          ? `활동제한 적용기간까지 ${penaltyInfo.accountName} 계정의\n포스팅 활동이 불가해요.`
                          : `미제출 3건이면 ${penaltyInfo.accountName} 계정의\n활동이 제한될 수 있으니 유의해 주세요.`,
                        iconColor: 'icon.danger',
                        iconSource: Danger,
                        title: penaltyInfo.isSuspended
                          ? '활동이 제한되었어요'
                          : '활동이 제한될 수 있어요',
                      });
                    }}
                    size='3XS'
                    source={InfoMarkLine}
                  />
                </Row>
              }
              rightContent={
                <Typography fontType='body2.regular'>
                  {penaltyInfo?.isSuspended
                    ? `${dayjs(penaltyInfo?.suspensionEndDate).format('YYYY.MM.DD')}까지`
                    : `${penaltyInfo.penaltyCount}건`}
                </Typography>
              }
              size={'S'}
              styledComponents={{
                root: {
                  bgColor: 'bg.dangerSubtle',
                  color: 'text.danger',
                  css: {
                    '&:hover': {
                      backgroundColor: getColorValue('bg.dangerSubtle', theme), // hover 시 배경색 변경 방지
                      border: '1px solid transparent', // hover 시 텍스트 색상 변경 방지
                      color: getColorValue('text.danger', theme),
                    },
                    hover: 'none',
                    pointerEvents: 'none',
                  },
                  justifyContent: 'space-between',
                  width: '100%',
                },
              }}
              variant='secondarySolid.error'
            />
          )}
        </Column>
      )}

      {isOpenManageModal && (
        <ManageModal
          exitHandler={() => {
            setIsOpenManageModal(false);
          }}
          isOpen={isOpenManageModal}
        />
      )}
    </Column>
  );
};
